// Cart builder section: sell a tier upgrade or an add-on on an email
// subscription the account already has. Account carts, card checkout only
// (the modal does not render it for a prospect or an invoice).
//
// Pick the subscription (from the account's products), then a new tier or an
// add-on and its seats. Every price shown is squish-backend's quote and every
// line added is the one it served (src/lib/emailChange.js).
import { useEffect, useState } from 'react';
import { listAccountProducts, getEmailChangeOptions } from '../api';
import {
  changeableEmailSubs, withEmailChange, emailChangeBlocker, addonUnavailableReason, upgradeSummary,
} from '../lib/emailChange';

export default function EmailChangeSection({ accountId, isInvoice, isProspect, items, setItems, ui }) {
  const { sectionLabel, miniSelect, addBtn, fmt } = ui;
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState(null);       // null loading | { error } | subs[]
  const [subId, setSubId] = useState('');
  const [kind, setKind] = useState('upgrade');  // 'upgrade' | 'addon'
  const [opts, setOpts] = useState(null);       // { subId, ...payload } | { subId, error }
  const [optsTry, setOptsTry] = useState(0);    // bumped by Retry
  const [targetSku, setTargetSku] = useState('');
  const [addonSku, setAddonSku] = useState('');
  const [seats, setSeats] = useState(1);
  const [addonQuote, setAddonQuote] = useState(null); // { key, ...quote } | { key, error }

  const sub = Array.isArray(subs) ? (subs.find(s => s.subscriptionId === subId) || subs[0] || null) : null;
  const mode = sub && !sub.canUpgrade ? 'addon' : sub && !sub.canAddon ? 'upgrade' : kind;
  const here = opts && sub && opts.subId === sub.subscriptionId ? opts : null;

  // The account's subscriptions, once the rep opens the section.
  useEffect(() => {
    if (!open || isProspect || !accountId || subs != null) return;
    let cancelled = false;
    listAccountProducts(accountId)
      .then(r => {
        if (cancelled) return;
        const list = changeableEmailSubs(r.products);
        // A short list (one adapter failed) is not proof there is nothing to sell.
        setSubs(list.length === 0 && r.productsComplete === false
          ? { error: 'some of their products could not be read' }
          : list);
      })
      .catch(e => { if (!cancelled) setSubs({ error: e.message || 'request failed' }); });
    return () => { cancelled = true; };
  }, [open, isProspect, accountId, subs]);

  // What the picked subscription can take.
  const pickedSubId = sub?.subscriptionId || '';
  useEffect(() => {
    if (!pickedSubId) return;
    let cancelled = false;
    setOpts(null);
    getEmailChangeOptions(accountId, pickedSubId)
      .then(r => { if (!cancelled) setOpts({ subId: pickedSubId, ...r }); })
      .catch(e => { if (!cancelled) setOpts({ subId: pickedSubId, error: e.message || 'request failed' }); });
    return () => { cancelled = true; };
  }, [accountId, pickedSubId, optsTry]);

  const quotes = Array.isArray(here?.upgrade?.quotes) ? here.upgrade.quotes : [];
  const eligible = quotes.filter(q => q.eligible && q.line);
  const quote = eligible.find(q => q.targetSku === targetSku) || eligible[0] || null;

  const addonOpts = Array.isArray(here?.addons?.options) ? here.addons.options : [];
  const available = addonOpts.filter(o => o.available);
  const addon = available.find(o => o.sku === addonSku) || available[0] || null;
  const maxSeats = Math.max(1, Number(here?.addons?.maxSeats) || 1);
  const seatCount = Math.min(Math.max(1, seats || 1), maxSeats);
  const quoteKey = addon ? `${pickedSubId}|${addon.sku}|${seatCount}` : '';

  // The add-on's price for the seats picked, from squish-backend.
  useEffect(() => {
    if (mode !== 'addon' || !quoteKey) return;
    let cancelled = false;
    const [sid, sku, n] = quoteKey.split('|');
    const t = setTimeout(() => {
      getEmailChangeOptions(accountId, sid, { addonSku: sku, seats: Number(n) })
        .then(r => { if (!cancelled) setAddonQuote({ key: quoteKey, ...(r.addonQuote || { error: 'no quote' }) }); })
        .catch(e => { if (!cancelled) setAddonQuote({ key: quoteKey, error: e.message || 'request failed' }); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [mode, accountId, quoteKey]);

  const aq = addonQuote?.key === quoteKey ? addonQuote : null;
  const line = mode === 'upgrade' ? quote?.line || null : aq?.line || null;
  const blocker = emailChangeBlocker({ isInvoice, isProspect, subs, items, line });

  // Why the picked choice can't be priced, when it can't.
  let reason = null;
  if (sub && !here) reason = 'Loading what this subscription can take…';
  else if (here?.error) reason = here.error;
  else if (mode === 'upgrade' && here?.upgrade?.error) reason = here.upgrade.error;
  else if (mode === 'upgrade' && here && !quote) reason = here.upgrade?.reason || 'No tier above this one can be bought on this subscription.';
  else if (mode === 'addon' && here?.addons?.error) reason = here.addons.error;
  else if (mode === 'addon' && here && !addon) reason = 'No add-on can be bought on this subscription.';
  else if (mode === 'addon' && addon && !aq) reason = 'Pricing…';
  else if (mode === 'addon' && aq?.error) reason = aq.error;

  const pill = (k, label, enabled) => (
    <button
      key={k}
      onClick={() => setKind(k)}
      disabled={!enabled}
      style={{ padding: '3px 9px', borderRadius: 3, border: 'none', cursor: enabled ? 'pointer' : 'default', fontSize: 10, fontWeight: 600, fontFamily: 'var(--mono)', background: mode === k ? 'var(--accent)' : 'transparent', color: mode === k ? '#fff' : 'var(--text3)', opacity: enabled ? 1 : 0.4 }}
    >
      {label}
    </button>
  );
  const note = { fontSize: 11, color: 'var(--text3)', lineHeight: 1.4 };
  const wide = { ...miniSelect, width: '100%', fontSize: 12, padding: '6px 8px' };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ ...sectionLabel, margin: 0 }}>Email subscription</div>
        {open && sub ? (
          <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--bg)', borderRadius: 4, border: '1px solid var(--border)' }}>
            {pill('upgrade', 'Upgrade tier', sub.canUpgrade)}
            {pill('addon', 'Add-on', sub.canAddon)}
          </div>
        ) : !open && (
          <button onClick={() => setOpen(true)} style={{ ...addBtn, background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)' }}>
            Upgrade or add to it
          </button>
        )}
      </div>
      {open && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, background: 'var(--bg3)', marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Array.isArray(subs) && subs.length > 0 && (
            <select value={sub?.subscriptionId || ''} onChange={e => { setSubId(e.target.value); setTargetSku(''); setAddonSku(''); }} style={wide}>
              {subs.map(s => (
                <option key={s.subscriptionId} value={s.subscriptionId}>{s.label}{s.domain ? ` · ${s.domain}` : ''}</option>
              ))}
            </select>
          )}

          {sub && here && !here.error && mode === 'upgrade' && quotes.length > 0 && (
            <select value={quote?.targetSku || ''} onChange={e => setTargetSku(e.target.value)} style={wide}>
              {quotes.map(q => (
                <option key={q.targetSku} value={q.targetSku} disabled={!q.eligible || !q.line}>
                  {q.targetLabel} · {q.eligible && q.line ? `${fmt(q.dueTodayCents / 100)} today` : q.reason || 'not available'}
                </option>
              ))}
            </select>
          )}
          {mode === 'upgrade' && quote && <div style={note}>{upgradeSummary(quote)}</div>}

          {sub && here && !here.error && mode === 'addon' && addonOpts.length > 0 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <select value={addon?.sku || ''} onChange={e => setAddonSku(e.target.value)} style={{ ...wide, flex: 1 }}>
                {addonOpts.map(o => (
                  <option key={o.sku} value={o.sku} disabled={!o.available}>
                    {o.label} · {fmt(o.perUserMonth)}/user/mo{o.available ? '' : ` · ${addonUnavailableReason(o)}`}
                  </option>
                ))}
              </select>
              <input
                type="number" min={1} max={maxSeats} value={seatCount}
                onChange={e => setSeats(parseInt(e.target.value, 10) || 1)}
                title={`Seats, up to the subscription's ${maxSeats}`}
                style={{ ...miniSelect, width: 56, fontSize: 12, padding: '6px 8px' }}
              />
            </div>
          )}

          {blocker || reason ? (
            <div style={note}>
              {blocker || reason}
              {(subs?.error || here?.error) && (
                <button onClick={() => (subs?.error ? setSubs(null) : setOptsTry(n => n + 1))} style={{ ...addBtn, marginLeft: 8, background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)' }}>Retry</button>
              )}
            </div>
          ) : (
            <button onClick={() => line && setItems(prev => withEmailChange(prev, line))} disabled={!line} style={{ ...addBtn, width: '100%', marginTop: 2, opacity: line ? 1 : 0.5 }}>
              + Add {line?.productName} · {fmt(line?.unitPrice || 0)} today
            </button>
          )}

          <div style={{ fontSize: 10, color: 'var(--text3)', lineHeight: 1.4 }}>
            {mode === 'upgrade'
              ? 'Moves every user to the new tier now. The price is for today: the unused-time credit runs down daily, so a link paid a day or more later is refused and needs a new cart.'
              : 'Adds the add-on for the seats picked, up to the subscription\'s seat count.'}
          </div>
        </div>
      )}
    </>
  );
}
